import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondary,
  Chip,
  Grid,
  TextField,
  InputLabel,
} from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from "@mui/lab";
import TimelineIcon from "@mui/icons-material/Timeline";
import ViewListIcon from "@mui/icons-material/ViewList";
import EditIcon from "@mui/icons-material/Edit";
import SortIcon from "@mui/icons-material/Sort";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import { useQuery } from '@tanstack/react-query';
import api from "../../../../../utils/apiConfig";

import logger from '../../../../../utils/logger';

// Define frequencies constant
const frequencies = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annual", label: "Annual" },
  { value: "one-time", label: "One-time" },
];

// Define categorizeGoal function
const categorizeGoal = (name) => {
  const lowercase = name.toLowerCase();
  if (lowercase.includes("retire")) return "Retirement";
  if (lowercase.includes("car") || lowercase.includes("vehicle"))
    return "Major Purchase";
  if (lowercase.includes("debt") || lowercase.includes("loan"))
    return "Debt Reduction";
  if (lowercase.includes("save") || lowercase.includes("savings"))
    return "Savings";
  if (lowercase.includes("education") || lowercase.includes("college"))
    return "Education";
  if (lowercase.includes("house") || lowercase.includes("home"))
    return "Housing";
  if (lowercase.includes("business")) return "Business";
  if (lowercase.includes("tax")) return "Tax Planning";
  if (lowercase.includes("estate")) return "Estate Planning";
  if (lowercase.includes("insurance")) return "Insurance";
  return "Other";
};

const GoalsTimeline = ({ goals = [], householdId }) => {
  const [viewMode, setViewMode] = useState("timeline");
  const [selectedUser, setSelectedUser] = useState("all");
  const [showAll, setShowAll] = useState(false);
  const [sortBy, setSortBy] = useState("date");
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    member: "Household",
    amount: "",
    start_date: "",
    target_date: "",
    priority: 1,
    category: "",
    frequency: "one-time",
    status: "active",
    metadata: {},
    goal_id: null,
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  // Fetch household members
  const { data: householdMembers = [] } = useQuery({
    queryKey: ["household-members", householdId],
    queryFn: () =>
      api
        .get(`/advisor-portal/client-database/household-profile/${householdId}`)
        .then((r) => r.data.members),
    enabled: !!householdId
  });

  // Filter and sort goals
  const filteredGoals = useMemo(() => {
    let filtered = goals.filter(
      (goal) =>
        selectedUser === "all" ||
        goal.user_id === selectedUser ||
        !goal.user_id,
    );

    // Sort by date or priority
    filtered.sort((a, b) => {
      if (sortBy === "date") {
        return moment(a.target_date).diff(moment(b.target_date));
      }
      return b.priority - a.priority;
    });

    // Limit to top 5 unless showAll is true
    return showAll ? filtered : filtered.slice(0, 5);
  }, [goals, selectedUser, sortBy, showAll]);

  const handleEditGoal = (goal) => {
    handleOpenModal(goal);
  };

  const handleOpenModal = (goal = null) => {
    if (goal) {
      setFormData({
        name: goal.name,
        member: goal.user_id ? String(goal.user_id) : "Household",
        amount: Number(goal.amount) || 0,
        start_date: goal.start_date || "",
        target_date: goal.target_date || "",
        priority: goal.priority || 1,
        category: goal.category || categorizeGoal(goal.name),
        frequency: goal.frequency || "one-time",
        status: goal.status || "active",
        metadata: goal.metadata || {},
        goal_id: goal.goal_id || goal.id,
      });
      setSelectedGoal(goal);
    } else {
      setFormData({
        name: "",
        member: "Household",
        amount: 0,
        start_date: "",
        target_date: "",
        priority: 1,
        category: "",
        frequency: "one-time",
        status: "active",
        metadata: {},
        goal_id: null,
      });
      setSelectedGoal(null);
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setSelectedGoal(null);
    setOpenModal(false);
  };

  const handleSaveGoal = () => {
    const goalData = {
      ...formData,
      user_id: formData.member === "Household" ? null : Number(formData.member),
      amount: Number(formData.amount),
    };

    // Rest of save logic...
    logger.info("Saving goal:", goalData);
    handleCloseModal();
  };

  const TimelineView = () => (
    <Box
      sx={{
        position: "relative",
        py: 1,
        maxWidth: "800px",
        margin: "0 auto",
      }}
      data-testid="timeline-card"
    >
      {/* Central vertical line */}
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: 0,
          bottom: 0,
          width: 2,
          bgcolor: "rgba(0, 0, 0, 0.12)",
          transform: "translateX(-50%)",
        }}
      />

      {filteredGoals.map((goal, index) => (
        <Box
          key={goal.id}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            position: "relative",
            minHeight: 100, // Reduced from 160
            mb: 2, // Add margin bottom for spacing
          }}
        >
          {/* Priority Diamond */}
          <Box
            sx={{
              width: 32, // Reduced from 40
              height: 32, // Reduced from 40
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2,
              "&::before": {
                content: '""',
                position: "absolute",
                width: "100%",
                height: "100%",
                transform: "rotate(45deg)",
                bgcolor:
                  goal.priority >= 4
                    ? "#f44336"
                    : goal.priority >= 3
                      ? "#ff9800"
                      : goal.priority >= 2
                        ? "#2196f3"
                        : "#4caf50",
                boxShadow: "0 0 0 2px white",
              },
            }}
          >
            <Typography
              sx={{
                color: "white",
                fontWeight: "bold",
                fontSize: "1rem", // Reduced from 1.1rem
                zIndex: 1,
              }}
            >
              {goal.priority}
            </Typography>
          </Box>

          {/* Date */}
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              position: "absolute",
              [index % 2 === 0 ? "right" : "left"]: "52%",
              top: -16, // Reduced from -20
              fontSize: "0.85rem", // Slightly smaller font
            }}
          >
            {moment(goal.target_date).format("MMM DD, YYYY")}
          </Typography>

          {/* Content Card */}
          <Box
            sx={{
              width: "42%", // Reduced from 45%
              [index % 2 === 0 ? "marginRight" : "marginLeft"]: "52%",
              bgcolor: "rgba(0, 0, 0, 0.02)",
              borderRadius: 1.5, // Reduced from 2
              p: 1.5, // Reduced from 2
              "&:hover": {
                bgcolor: "rgba(0, 0, 0, 0.04)",
                transition: "background-color 0.3s ease",
              },
            }}
          >
            <Box className="flex justify-between items-start mb-1">
              <Typography
                variant="subtitle1" // Changed from h6
                component="h3"
                sx={{ fontSize: "1rem", fontWeight: "bold" }}
              >
                {goal.name}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleEditGoal(goal)}
                sx={{ padding: 0.5 }} // Reduced padding
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ fontSize: "0.85rem", mb: 1 }}
            >
              Target Amount: ${goal.amount.toLocaleString()}
            </Typography>
            <Box className="flex gap-0.5 mt-1">
              <Chip
                size="small"
                label={goal.category}
                color={
                  goal.priority >= 4
                    ? "error"
                    : goal.priority >= 3
                      ? "warning"
                      : goal.priority >= 2
                        ? "primary"
                        : "success"
                }
                sx={{ height: 20, fontSize: "0.75rem" }} // Smaller chips
              />
              {goal.user_id && (
                <Chip
                  size="small"
                  label={
                    householdMembers.find((m) => m.userId === goal.user_id)
                      ?.firstName || "Unknown"
                  }
                  variant="outlined"
                  sx={{ height: 20, fontSize: "0.75rem" }}
                />
              )}
              <Chip
                size="small"
                label={goal.frequency}
                variant="outlined"
                sx={{ height: 20, fontSize: "0.75rem" }}
              />
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );

  const ListView = () => (
    <List>
      {filteredGoals.map((goal) => (
        <ListItem
          key={goal.id}
          secondaryAction={
            <IconButton edge="end" onClick={() => handleEditGoal(goal)}>
              <EditIcon />
            </IconButton>
          }
        >
          <ListItemText
            primary={goal.name}
            secondary={
              <Box className="flex gap-2 mt-1">
                <Typography variant="body2" color="textSecondary">
                  {moment(goal.target_date).format("MMM DD, YYYY")}
                </Typography>
                <Chip
                  size="small"
                  label={`Priority: ${goal.priority}`}
                  color={
                    goal.priority >= 4
                      ? "error"
                      : goal.priority >= 3
                        ? "warning"
                        : "default"
                  }
                />
                {goal.user_id && (
                  <Chip
                    size="small"
                    label={
                      householdMembers.find((m) => m.userId === goal.user_id)
                        ?.firstName || "Unknown"
                    }
                  />
                )}
              </Box>
            }
          />
        </ListItem>
      ))}
    </List>
  );

  // Add these constants for the form
  const categories = [
    "Retirement",
    "Major Purchase",
    "Debt Reduction",
    "Savings",
    "Education",
    "Housing",
    "Business",
    "Tax Planning",
    "Estate Planning",
    "Insurance",
    "Other",
  ];

  const statuses = ["active", "completed", "deferred", "cancelled"];

  return (
    <Box className="h-full">
      {/* Controls */}
      <Box className="flex justify-between items-center gap-4 mb-4">
        <Box className="flex items-center gap-2">
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, value) => value && setViewMode(value)}
            size="small"
          >
            <ToggleButton value="timeline">
              <TimelineIcon className="mr-1" /> Timeline
            </ToggleButton>
            <ToggleButton value="list">
              <ViewListIcon className="mr-1" /> List
            </ToggleButton>
          </ToggleButtonGroup>

          <FormControl size="small" className="min-w-[120px]">
            <Select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
            >
              <MenuItem value="all">All Members</MenuItem>
              {householdMembers.map((member) => (
                <MenuItem key={member.userId} value={member.userId}>
                  {member.firstName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box className="flex items-center gap-2">
          <Button
            size="small"
            startIcon={<SortIcon />}
            onClick={() => setSortBy(sortBy === "date" ? "priority" : "date")}
          >
            Sort by {sortBy === "date" ? "Priority" : "Date"}
          </Button>
          {!showAll && filteredGoals.length >= 5 && (
            <Button size="small" onClick={() => setShowAll(true)}>
              View All
            </Button>
          )}
        </Box>
      </Box>

      {/* Goals Display */}
      {viewMode === "timeline" ? <TimelineView /> : <ListView />}

      {/* Improved Add/Edit Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="flex justify-between items-center">
          {selectedGoal ? "Edit Goal" : "Add New Goal"}
          <IconButton onClick={handleCloseModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} className="mt-0">
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" className="font-semibold mb-2">
                Basic Information
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Goal Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  label="Category"
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Timing and Amount */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" className="font-semibold mb-2">
                Timing and Amount
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.start_date}
                onChange={(e) =>
                  setFormData({ ...formData, start_date: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Target Date"
                type="date"
                value={formData.target_date}
                onChange={(e) =>
                  setFormData({ ...formData, target_date: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Target Amount"
                type="number"
                value={formData.amount}
                onChange={(e) =>
                  setFormData({ ...formData, amount: e.target.value })
                }
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={formData.frequency}
                  onChange={(e) =>
                    setFormData({ ...formData, frequency: e.target.value })
                  }
                  label="Frequency"
                >
                  {frequencies.map((freq) => (
                    <MenuItem key={freq.value} value={freq.value}>
                      {freq.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Assignment and Priority */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" className="font-semibold mb-2">
                Assignment and Priority
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Member</InputLabel>
                <Select
                  value={formData.member}
                  onChange={(e) =>
                    setFormData({ ...formData, member: e.target.value })
                  }
                  label="Member"
                >
                  <MenuItem value="Household">Household</MenuItem>
                  {householdMembers.map((member) => (
                    <MenuItem key={member.userId} value={String(member.userId)}>
                      {member.firstName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) =>
                    setFormData({ ...formData, priority: e.target.value })
                  }
                  label="Priority"
                >
                  {[1, 2, 3, 4, 5].map((p) => (
                    <MenuItem key={p} value={p}>
                      {p} - {p === 1 ? "Low" : p === 5 ? "Critical" : "Medium"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Status */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" className="font-semibold mb-2">
                Status
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                  label="Status"
                >
                  {statuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions className="p-4">
          <Button onClick={handleCloseModal} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSaveGoal}
            variant="contained"
            className="bg-blue-600"
          >
            {selectedGoal ? "Save Changes" : "Add Goal"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GoalsTimeline;
